import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';

// Inline SVG Logo Component - Hand-drawn rooster style
const LogoIcon = () => (
  <svg width="45" height="45" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    {/* Black background */}
    <rect width="100" height="100" fill="#000000"/>
    
    {/* Chalk-style white border */}
    <rect x="2" y="2" width="96" height="96" fill="none" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
    
    {/* Rooster head */}
    <circle cx="50" cy="30" r="10" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round"/>
    
    {/* Rooster comb (wavy) */}
    <path d="M 40 20 Q 45 15 50 18 Q 55 15 60 20" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round"/>
    
    {/* Rooster eye */}
    <circle cx="45" cy="25" r="1.5" fill="#FFFFFF"/>
    
    {/* Rooster beak (open - crowing) */}
    <path d="M 60 30 L 70 25 L 70 35 Z" fill="#FFFFFF"/>
    
    {/* Rooster wattle */}
    <path d="M 45 35 Q 40 40 45 45" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
    
    {/* Rooster body */}
    <ellipse cx="50" cy="50" rx="15" ry="8" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round"/>
    
    {/* Rooster wing (suggested by curved lines) */}
    <path d="M 35 45 Q 30 40 35 35" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M 37 47 Q 32 42 37 37" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M 39 49 Q 34 44 39 39" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
    
    {/* Rooster tail feathers (fanning out) */}
    <path d="M 65 50 L 80 40 L 75 60" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M 63 52 L 78 42 L 73 62" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M 61 54 L 76 44 L 71 64" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
    
    {/* Rooster legs */}
    <line x1="40" y1="58" x2="40" y2="75" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round"/>
    <line x1="60" y1="58" x2="60" y2="75" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round"/>
    
    {/* Text: POULE & POULETTE */}
    <text x="50" y="90" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="8" fontWeight="bold" fill="#FFFFFF" letterSpacing="0.5">
      POULE & POULETTE
    </text>
  </svg>
);

const HeaderContainer = styled.header`
  background: #ffffff;
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 2px solid #f0f0f0;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-size: 1.8rem;
  font-weight: bold;
  color: #000000;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Arial', sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  /* Hand-drawn chalk style effect */
  text-shadow: 
    1px 1px 0px rgba(255, 255, 255, 0.8),
    -1px -1px 0px rgba(255, 255, 255, 0.8),
    1px -1px 0px rgba(255, 255, 255, 0.8),
    -1px 1px 0px rgba(255, 255, 255, 0.8);
  
  &:hover {
    color: #FFD700;
    text-shadow: 
      1px 1px 0px rgba(255, 215, 0, 0.8),
      -1px -1px 0px rgba(255, 215, 0, 0.8),
      1px -1px 0px rgba(255, 215, 0, 0.8),
      -1px 1px 0px rgba(255, 215, 0, 0.8);
  }
`;

const LogoContainer = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 8px;
  background: #000000;
  border: 2px solid #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const NavLinks = styled.div<{ isOpen: boolean }>`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => props.isOpen ? '0' : '-100%'};
    height: 100vh;
    width: 250px;
    background: white;
    flex-direction: column;
    padding: 2rem;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
    z-index: 1001;
  }
`;

const NavLink = styled(Link)<{ active: boolean }>`
  color: #000000;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background: #f0f0f0;
    color: #FFD700;
  }

  ${props => props.active && `
    background: #FFD700;
    color: #000000;
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 20px;
      height: 2px;
      background: #FFD700;
      border-radius: 1px;
    }
  `}

  @media (max-width: 768px) {
    color: #000000;
    width: 100%;
    text-align: center;
    
    &:hover {
      background: #f0f0f0;
      color: #FFD700;
    }
    
    ${props => props.active && `
      background: #FFD700;
      color: #000000;
    `}
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #000000;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const CloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #333;
  font-size: 1.5rem;
  cursor: pointer;
  position: absolute;
  top: 1rem;
  right: 1rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
`;

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/menu', label: 'Menu' },
    { path: '/reserveren', label: 'Reserveren' },
    { path: '/takeaway', label: 'Takeaway' },
    { path: '/restaurants', label: 'Vestigingen' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      <HeaderContainer>
        <Nav>
          <Logo to="/">
            <LogoContainer>
              <LogoIcon />
            </LogoContainer>
            Poule & Poulette
          </Logo>
          
          <NavLinks isOpen={isMenuOpen}>
            <CloseButton onClick={closeMenu}>
              <X />
            </CloseButton>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                active={location.pathname === item.path}
                onClick={closeMenu}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>
          
          <MobileMenuButton onClick={toggleMenu}>
            <Menu />
          </MobileMenuButton>
        </Nav>
      </HeaderContainer>
      <Overlay isOpen={isMenuOpen} onClick={closeMenu} />
    </>
  );
};

export default Header;
